import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLazyDataLoader } from '@europrocurement/l2d-modules/hooks';

import { useBillingRender } from '../../../hooks';
import { useCollectionBillingCtx } from '../../../../../providers/CollectionBillingProvider';
import useDossierFacturationActions from './hooks/useDossierFacturationActions';
import { heightFooterFacturation } from './functions';
import OrderDetailsSection from './components/OrderDetailsSection/OrderDetailsSection';
import EncaissementsSection from './components/EncaissementsSection/EncaissementsSection';
import ProvisionsSection from './components/ProvisionsSection/ProvisionsSection';
import TotalsSection from './components/TotalsSection/TotalsSection';

const DossierFacturation: React.FunctionComponent = function () {
    const { onChangeDossiersSelected } = useDossierFacturationActions();
    const {
        getCollections,
        getCollectionInvoices,
        getAcomptes,
        getReglements,
        isDataPrefacturationLoaded,
        isBillingDossierIdsLoaded,
        isAcomptesLoaded,
        isReglementsLoaded,
    } = useCollectionBillingCtx();
    const collections = getCollections();

    const { hasActiveInvoice } = useBillingRender();

    const invoices = getCollectionInvoices();
    const { loadDossierPrefacturation, loadCollectionProvision, loadCollectionReglement } =
        useLazyDataLoader();

    const acomptes = getAcomptes();
    const reglements = getReglements();

    useEffect(() => {
        if (collections.length === 0) {
            return;
        }

        const collectionIds = collections.map((c) => c.id);

        if (!isDataPrefacturationLoaded() && !isBillingDossierIdsLoaded()) {
            loadDossierPrefacturation(collections.map((c) => c.dossierIds).flat());
        }

        if (!isAcomptesLoaded()) {
            loadCollectionProvision(collectionIds);
        }

        if (!isReglementsLoaded()) {
            loadCollectionReglement(collectionIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collections]);

    const acompteAttendu = parseFloat(
        collections
            .reduce((total, collection) => total + (collection.acompteAttendu ?? 0), 0)
            .toFixed(2),
    );

    const totalAcomptes = acomptes.reduce(
        (total, acompte) => total + (acompte.montant ? acompte.montant : 0),
        0,
    );

    const totalReglements = reglements.reduce(
        (total, reglement) => total + (reglement.montant ? reglement.montant : 0),
        0,
    );

    const dataLoaded = isDataPrefacturationLoaded() && isBillingDossierIdsLoaded();

    return dataLoaded ? (
        <Box
            sx={{
                marginBottom: heightFooterFacturation,
            }}
        >
            <OrderDetailsSection
                collections={collections}
                onChangeDossiersSelected={onChangeDossiersSelected}
            />

            <ProvisionsSection
                acompteAttendu={acompteAttendu}
                totalAcomptes={totalAcomptes}
                totalReglements={totalReglements}
                collections={collections}
                hasActiveInvoice={hasActiveInvoice(invoices)}
            />

            <EncaissementsSection
                collections={collections}
                totalAcomptes={totalAcomptes}
                totalReglements={totalReglements}
                reglements={reglements}
                acomptes={acomptes}
            />

            <TotalsSection
                totalAcomptes={totalAcomptes}
                acomptes={acomptes as unknown as Parameters<typeof TotalsSection>[0]['acomptes']}
                collections={collections}
            />
        </Box>
    ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <CircularProgress />
        </Box>
    );
};

export default DossierFacturation;
